/**
 * Trafiklab API service for fetching GTFS Realtime vehicle positions
 */

import GtfsRealtimeBindings from 'gtfs-realtime-bindings';
import { API_CONFIG } from '../config';
import { getRoutesData, getTripsData } from './gtfs-static';
import type { VehiclePosition, Vehicle, RouteInfo, RoutesData, TripsData } from '../types/gtfs-rt';

/**
 * Fetch vehicle positions from Trafiklab GTFS-RT API
 */
export async function fetchVehiclePositions(operator: string, apiKey: string): Promise<VehiclePosition[]> {
  const endpoint = `${API_CONFIG.BASE_URL}/gtfs-rt/${operator}/VehiclePositions.pb?key=${apiKey}`;

  try {
    const response = await fetch(endpoint, {
      headers: API_CONFIG.HEADERS
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    const buffer = await response.arrayBuffer();
    const feed = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(
      new Uint8Array(buffer)
    );

    const vehicles: VehiclePosition[] = [];

    feed.entity.forEach(entity => {
      const vehicle = entity.vehicle;
      if (!vehicle || !vehicle.position) return;

      // Handle timestamp - could be a Long object or a number
      let timestamp = Date.now();
      if (vehicle.timestamp) {
        if (typeof vehicle.timestamp === 'number') {
          timestamp = vehicle.timestamp * 1000;
        } else if (typeof vehicle.timestamp.toNumber === 'function') {
          timestamp = vehicle.timestamp.toNumber() * 1000;
        }
      }

      vehicles.push({
        id: entity.id,
        tripId: vehicle.trip?.tripId || null,
        routeId: vehicle.trip?.routeId || null,
        position: {
          latitude: vehicle.position.latitude,
          longitude: vehicle.position.longitude,
          bearing: vehicle.position.bearing,
          speed: vehicle.position.speed
        },
        timestamp,
        vehicleId: vehicle.vehicle?.id,
        vehicleLabel: vehicle.vehicle?.label,
        currentStatus: vehicle.currentStatus,
        stopId: vehicle.stopId,
        directionId: vehicle.trip?.directionId
      });
    });

    return vehicles;
  } catch (error) {
    console.error('Error fetching vehicle positions:', error);
    throw error;
  }
}

/**
 * Get route information for a vehicle
 */
function getRouteInfo(
  vehiclePosition: VehiclePosition,
  routesData: RoutesData,
  tripsData: TripsData
): RouteInfo | null {
  const routes = routesData.routes;
  const trips = tripsData.trips;

  // First try to use the route_id from the vehicle position
  let routeId = vehiclePosition.routeId;

  // If no route_id, try to look it up using trip_id
  if (!routeId && vehiclePosition.tripId) {
    routeId = trips[vehiclePosition.tripId] || null;
  }

  // Look up route information
  if (routeId && routes[routeId]) {
    return routes[routeId];
  }

  return null;
}

/**
 * Transform GTFS-RT vehicle positions into deck.gl-ready format with colors
 */
export function transformVehicles(
  vehiclePositions: VehiclePosition[],
  routesData: RoutesData,
  tripsData: TripsData
): Vehicle[] {
  const defaultColor = '#8C8C8C';

  return vehiclePositions.map(vp => {
    const routeInfo = getRouteInfo(vp, routesData, tripsData);

    return {
      id: vp.id,
      position: [vp.position.longitude, vp.position.latitude],
      bearing: vp.position.bearing ?? 0,
      speed: vp.position.speed ?? 0,
      timestamp: vp.timestamp,
      routeId: vp.routeId,
      routeShortName: routeInfo?.shortName || 'Unknown',
      routeLongName: routeInfo?.longName || '',
      vehicleType: routeInfo?.type || 'default',
      color: routeInfo?.color || defaultColor
    };
  });
}

/**
 * Fetch and transform vehicle positions in one call
 */
export async function getVehicles(
  operator: string,
  realtimeApiKey: string,
): Promise<Vehicle[]> {
  // Fetch static data (will be cached by gtfs-static service)
  const [routesData, tripsData] = await Promise.all([
    getRoutesData(operator),
    getTripsData(operator)
  ]);

  const positions = await fetchVehiclePositions(operator, realtimeApiKey);
  return transformVehicles(positions, routesData, tripsData);
}
